import { fromJS } from 'immutable';
import { change } from 'redux-form';
import { Dispatch } from 'redux';

import { CallActionPayload, MethodResponse } from '../../types';

export const setFormFields = async (
  payload: CallActionPayload,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dispatch: Dispatch<any>
): Promise<MethodResponse> => {
  const { form, fields, streamingDelay } = payload as {
    form: string;
    fields: Record<string, unknown>;
    streamingDelay?: number;
  };

  if (!form) {
    throw new Error('form is required for set-form-fields method');
  }

  if (!fields) {
    throw new Error('fields is required for set-form-fields method');
  }

  const isUrl = (str: string): boolean => {
    const urlPattern = /^https?:\/\/|^www\./;
    return urlPattern.test(str);
  };

  const streamString = async (fieldName: string, value: string, delay: number): Promise<void> => {
    if (isUrl(value)) {
      // Don't stream URLs, set them immediately
      dispatch(change(String(form), fieldName, value));
      return;
    }

    // Clear the field first
    dispatch(change(String(form), fieldName, ''));

    const streamChunks = async (currentValue: string, remainingText: string): Promise<void> => {
      if (remainingText.length === 0) {
        return;
      }

      // Random chunk size between 2 and 5 characters
      const chunkSize = Math.floor(Math.random() * 4) + 2;
      const chunk = remainingText.slice(0, chunkSize);
      const newRemainingText = remainingText.slice(chunkSize);
      const newCurrentValue = currentValue + chunk;

      dispatch(change(String(form), fieldName, newCurrentValue));

      if (newRemainingText.length > 0) {
        await new Promise((resolve) => setTimeout(resolve, delay));
        await streamChunks(newCurrentValue, newRemainingText);
      }
    };

    await streamChunks('', value);
  };

  const makeImmutable = async (fieldName: string, value: unknown): Promise<void> => {
    // Handle string streaming
    if (typeof value === 'string' && streamingDelay && streamingDelay > 0) {
      await streamString(fieldName, value, streamingDelay);
      return;
    }

    // Convert complex data types (arrays and objects) to Immutable format
    let processedValue = value;
    if (
      value !== null &&
      (Array.isArray(value) || (typeof value === 'object' && value.constructor === Object))
    ) {
      processedValue = fromJS(value);
    }

    dispatch(change(String(form), fieldName, processedValue));
  };

  const processAllFields = async () => {
    const entries = Object.entries(fields);

    // Process fields sequentially to maintain order and avoid overwhelming the form
    await entries.reduce(async (promise, [fieldName, fieldValue]) => {
      await promise;
      return makeImmutable(fieldName, fieldValue);
    }, Promise.resolve());
  };

  await processAllFields();

  return {};
};
